import { getAuth, updateEmail } from "firebase/auth";
import { FC, useCallback, useMemo, useState } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { useNavigate } from "react-router";

import { AccountSettingsPageContainer_Mutation } from "~/src/__relay_artifacts__/AccountSettingsPageContainer_Mutation.graphql";
import { AccountSettingsPageContainer_Query } from "~/src/__relay_artifacts__/AccountSettingsPageContainer_Query.graphql";
import { useUser } from "~/src/contexts";
import { useFormErrorHandler, useOpenState } from "~/src/lib/hooks";
import { useMutationCommit } from "~/src/lib/react-relay";

import { AccountSettingsPage, AccountSettingsPageProps } from "./presentations";

const query = graphql`
  query AccountSettingsPageContainer_Query {
    viewer {
      role
      name
      company
      tel
      email
      itpUrls
      userTag
      isCrossDomainQuery
      isCrossDomainFragment
      jsTagManagementEnabled
      enableBackendUrlMapping
      sites(first: 1) {
        nodes {
          slug
        }
      }
    }
  }
`;

const mutation = graphql`
  mutation AccountSettingsPageContainer_Mutation($input: UpdateUserInput!) {
    updateUser(input: $input) {
      user {
        name
        company
        tel
        email
        itpUrls
        userTag
        isCrossDomainQuery
        isCrossDomainFragment
        jsTagManagementEnabled
        enableBackendUrlMapping
      }
    }
  }
`;

export const AccountSettingsPageContainer: FC<
  React.PropsWithChildren<React.PropsWithChildren<unknown>>
> = () => {
  const userContext = useUser();
  if (userContext === null) throw new Error("user context must be set.");
  const { setUser } = userContext;

  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useOpenState();
  const [reAuthenticated, setReAuthenticated] = useState(false);

  const modalProps = {
    reAuthenticated,
    isOpen,
    onClose,
    handleReAuthenticationStatus: (status: boolean) =>
      setReAuthenticated(status),
  };

  const { viewer: user } = useLazyLoadQuery<AccountSettingsPageContainer_Query>(
    query,
    {}
  );

  const scriptTagSettingsPath = useMemo(() => {
    const slug = user.sites.nodes?.[0]?.slug;
    if (slug == null) return null;
    return `/sites/${slug}/settings?tab=script_tag`;
  }, [user.sites.nodes]);

  const initialValues = useMemo<AccountSettingsPageProps["initialValues"]>(
    () => ({
      company: user.company || "",
      tel: user.tel || "",
      name: user.name || "",
      email: user.email,
      itpUrls: user.itpUrls || "",
      userTag: user.userTag || undefined,
      isCrossDomainQuery: user.isCrossDomainQuery,
      isCrossDomainFragment: user.isCrossDomainFragment,
      jsTagManagementEnabled: user.jsTagManagementEnabled,
      enableBackendUrlMapping: user.enableBackendUrlMapping,
      reAuthPassword: "",
    }),
    [user]
  );

  const handleCancel = useCallback(() => navigate(-1), [navigate]);
  const { onFormError } = useFormErrorHandler();

  const mutate =
    useMutationCommit<AccountSettingsPageContainer_Mutation>(mutation);

  const handleSubmit = useCallback<AccountSettingsPageProps["onSubmit"]>(
    async ({ ...values }, { setErrors }) => {
      try {
        const auth = getAuth();
        const authUser = auth.currentUser;
        if (!authUser) throw new Error("認証されたユーザーが見つかりません!");

        const emailChanged = authUser?.email !== values.email;
        if (emailChanged) {
          if (reAuthenticated) {
            // Update email on firebase
            await updateEmail(authUser, values.email);
          } else {
            onOpen();
            return false;
          }
        }
        const { reAuthPassword, userTag, ...updateValues } = values;

        const res = await mutate({
          variables: {
            input: {
              ...updateValues,
              // Dual-edit is rejected when module management is on
              ...(updateValues.jsTagManagementEnabled ? {} : { userTag }),
            },
          },
        });
        const user = res.updateUser?.user;
        if (!user) throw new Error("assertion failed");

        // Request a new idToken after updating email on firebase and database
        if (emailChanged) {
          const forceRefresh = true;
          const newIdToken = await authUser.getIdToken(forceRefresh);
          // Update user state
          setUser({
            ...authUser, // Get other user details from auth object
            idToken: newIdToken,
            email: values.email,
          });
        }

        navigate("/account");
      } catch (err) {
        onFormError(err, setErrors);
      }
    },
    [mutate, navigate, onFormError, onOpen, reAuthenticated, setUser]
  );

  return (
    <AccountSettingsPage
      initialValues={initialValues}
      onCancelClick={handleCancel}
      onSubmit={handleSubmit}
      modalProps={{ ...modalProps }}
      scriptTagSettingsPath={scriptTagSettingsPath}
    />
  );
};
